import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import OptionsMenu from "./optionsMenu";
import {
  RowType,
  ParentTree,
  AttributesTableProps,
  SegmentNode,
  SegmentRowProps,
} from "./campaign-interface";
import { ReactComponent as FolderIcon }from "../../assets/icons/folder.svg";
import { ReactComponent as SubFolderIcon } from "../../assets/icons/subfolder.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/icons/arrow_right.svg";
import dayjs from "dayjs";
import { useSimulationParams } from "../../shared/hooks/useSimulationParams";

function flattenCampaigns(
  parents: ParentTree[],
  level: number = 0,
  parentKey: string = ""
): RowType[] {

  const rows: RowType[] = [];
  try {
    parents.forEach((parent, parentIndex) => {
      const parentName = parent.country?.trim() || `Folder ${parentIndex + 1}`;
      const parentKeyName = `${parentName}-${parentIndex}`;
      rows.push({
        id: null,
        key: parentKeyName,
        name: parentName,
        market: parent.country || "-",
        status: "",
        lastEdited: "",
        destinations: "",
        consumer: "",
        level: 0,
        isFolder: true,
        isOpen: false,
        children: parent.children
          ? flattenChildren(parent.children, 1, parentKeyName, parent.country || "-", parentName)
          : [],
      });
    });
  } catch (error) {
    console.error("Error flattening campaign:", error);
  }
  return rows;
}

function flattenChildren(
  nodes: SegmentNode[],
  level: number,
  parentKey: string,
  market: string,
  Destinations: string
): RowType[] {
  return nodes.map((node, index) => {
    const key = `${parentKey}-${node.name}-${index}`;
    const isFolder = node.type === "folder";

    return {
      id: node.id || null,
      market: market,
      key,
      name: node.name,
      status: node.status || "-",
      cadence: node.cadence || "-",
      launch_date: node.launch_date ? dayjs.utc(node.launch_date).tz(dayjs.tz.guess()).format("MMM DD, YYYY") : "-",
      lastEdited: node.last_modified ? dayjs.utc(node.last_modified).tz(dayjs.tz.guess()).format("MMM DD, YYYY") : "-",
      destinations: Array.isArray((node as any).Destinations)
        ? (node as any).Destinations.join(", ")
        : "-",
      consumer: typeof node.count === "number" ? node.count.toString() : "-",
      level,
      isFolder,
      isOpen: false,
      children: node.children?.length
        ? flattenChildren(node.children, level + 1, key, market, Destinations)
        : [],
    };
  });
}

function SegmentRow({ row, isOpen, toggleOpen, countryAdminMap }: SegmentRowProps) {
  const indent = row.level * 24;
  const { createLink } = useSimulationParams();
  let icon = null;

  if (row.isFolder || row.level === 0) {
    icon =
      row.level === 0 ? (
        <FolderIcon
          title="Folder"
          style={{ width: 20, height: 20, marginRight: 8 }}
        />
      ) : (
        <SubFolderIcon
          title="Subfolder"
          style={{ width: 20, height: 20, marginRight: 8 }}
        />
      );
  }

  const handleRowClick = () => {
    if (row.isFolder) {
      toggleOpen(row.key);
    }
  };

  const arrowIcon = (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        toggleOpen(row.key);
      }}
      sx={{
        p: 0.5,
        mr: 0.5,
      }}
    >
      <ArrowDownIcon
        title={isOpen ? "Collapse" : "Expand"}
        style={{
          width: 16,
          height: 16,
          transform: isOpen ? "rotate(0deg)" : "rotate(270deg)",
          transition: "transform 0.2s",
        }}
      />
    </IconButton>
  );
  return (
    <TableRow
      hover
      component={!row.isFolder ? Link : "tr"}
      {...(!row.isFolder && row.id
        ? { to: createLink(`/campaign/${encodeURIComponent(row.id)}`) }
        : {})}
      onClick={row.isFolder ? handleRowClick : undefined}
      style={{ cursor: "pointer", textDecoration: "none", color: "inherit" }}
    >
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", pl: `${indent}px` }}>
          {row.isFolder ? arrowIcon : <Box sx={{ width: 32 }} />}
          {icon}
          <Typography
            variant="body2"
            sx={{
              fontSize: "calc(14 / var(--base-font) * 1rem)",
              fontWeight: row.level === 0 ? 700 : row.level === 3 ? 500 : 400,
              color: row.level === 0 ? "#e6007a" : "#222",
            }}
          >
            {row.level === 0 ? row.name : row.isFolder ? `${row.name}` : `${row.name}`}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <span>{row.status}</span>
        </Box>
      </TableCell>
      <TableCell>{row.launch_date || "-"}</TableCell>
      <TableCell>{row.cadence || "-"}</TableCell>
      <TableCell
        sx={{
          maxWidth: 200,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.destinations}
      </TableCell>
      <TableCell>{row.consumer}</TableCell>
      <TableCell
        key="options-menu"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          textAlign: "right",
          cursor: "default",
          "&:hover": {
            backgroundColor: "#f5f5f5",
          },
          "&:focus": {
            backgroundColor: "#f5f5f5",
          },
          "&:active": {
            backgroundColor: "#e0e0e0",
          },
        }}
      >
        {row.isFolder || row.level === 0 ? null : (() => {
          return (
            <Box>
              {(() => {
                return (
                  countryAdminMap &&
                  Object.keys(countryAdminMap).some(
                    (country) => country.toLowerCase() === row.market?.toLowerCase() && countryAdminMap[country] === 1
                  ) ? (
                    <OptionsMenu id={row.id} name={row.name} status={row.status} />
                  ) : null
                );
              })()}
            </Box>
          );
        })()}
      </TableCell>
    </TableRow>
  );
}

function toggleIsOpen(rows: RowType[], key: string): RowType[] {
  return rows.map((row) => {
    if (row.key === key) {
      return { ...row, isOpen: !row.isOpen };
    }
    if (row.children) {
      return { ...row, children: toggleIsOpen(row.children, key) };
    }
    return row;
  });
}

function CampaignsTableTree({ data, countryAdminMap, stickyTop }: AttributesTableProps) {
  const theme = useTheme();

  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  const isSm = useMediaQuery(theme.breakpoints.only("sm"));
  const isMd = useMediaQuery(theme.breakpoints.only("md"));
  const isLg = useMediaQuery(theme.breakpoints.only("lg"));

  const getColumnWidths = () => {
    if (isXs) {
      return {
        name: "30%",
        status: "12%",
        lastEdited: "13%",
        market: "10%",
        campaign: "25%",
        consumer: "10%",
        actions: "5%",
      };
    }
    if (isSm) {
      return {
        name: "28%",
        status: "12%",
        lastEdited: "15%",
        market: "10%",
        campaign: "25%",
        consumer: "10%",
        actions: "5%",
      };
    }
    if (isMd) {
      return {
        name: "24%",
        status: "13%",
        lastEdited: "15%",
        market: "10%",
        campaign: "22%",
        consumer: "10%",
        actions: "5%",
      };
    }
    if (isLg) {
      return {
        name: "35%",
        status: "8%",
        lastEdited: "10%",
        market: "15%",
        campaign: "17%",
        consumer: "10%",
        actions: "5%",
      };
    }
    return {
        name: "35%",
        status: "8%",
        lastEdited: "10%",
        market: "15%",
        campaign: "17%",
        consumer: "10%",
        actions: "5%",
    };
  };

  const colWidths = getColumnWidths();

  const [rows, setRows] = useState<RowType[]>([]);
  const [allExpanded, setAllExpanded] = useState(false);

  function setAllRowsOpen(rows: RowType[], open: boolean): RowType[] {
    return rows.map((row) => ({
      ...row,
      isOpen: open,
      children: row.children ? setAllRowsOpen(row.children, open) : [],
    }));
  }

  const arrowIconHeader = (
    <IconButton
      size="small"
      onClick={(e) => {
        e.stopPropagation();
        const newState = !allExpanded;
        setAllExpanded(newState);

        // Update openMap for consistency
        const updatedMap: Record<string, boolean> = {};
        const updateMap = (rows: RowType[]) => {
          rows.forEach((row) => {
            if (row.children && row.children.length) {
              updatedMap[row.key] = newState;
              updateMap(row.children);
            }
          });
        };
        updateMap(rows);
        setOpenMap(updatedMap);
        setRows((prevRows) => setAllRowsOpen(prevRows, newState));
      }}
      sx={{ p: 0.5, mr: 0.5 }}
    >
      <ArrowDownIcon
        title={allExpanded ? "Collapse all" : "Expand all"}
        style={{
          width: 16,
          height: 16,
          transform: allExpanded ? "rotate(0deg)" : "rotate(270deg)",
          transition: "transform 0.2s",
        }}
      />
    </IconButton>
  );

  useEffect(() => {
    if (Array.isArray(data)) {
      setRows(flattenCampaigns(data as ParentTree[]));
    }
  }, [data]);

  const [openMap, setOpenMap] = useState<Record<string, boolean>>({});
  const toggleOpen = (key: string) => {
    setOpenMap((prev) => ({ ...prev, [key]: !prev[key] }));
    setRows((prevRows) => toggleIsOpen(prevRows, key));
  };

  const renderRows = (rows: RowType[]): React.ReactElement[] => {
    return rows.flatMap((row) => {
      const rowElement = (
        <SegmentRow
          key={row.key}
          isOpen={!!openMap[row.key]}
          row={row}
          toggleOpen={toggleOpen}
          countryAdminMap={countryAdminMap}
        />
      );

      if (row.isFolder && openMap[row.key] && row.children) {
        return [rowElement, ...renderRows(row.children)];
      }

      return [rowElement];
    });
  };

  return (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead sx={{ "--table-sticky-top": `${stickyTop}px` } as React.CSSProperties}>
          <TableRow>
            <TableCell className="cell-name" sx={{ width: colWidths.name, minWidth: 100 }}>
              {arrowIconHeader} Name
            </TableCell>
            <TableCell className="cell-status" sx={{ width: colWidths.status, minWidth: 60 }}>
              Status
            </TableCell>
            <TableCell
              className="cell-last_edited"
              sx={{ width: colWidths.lastEdited, minWidth: 80 }}
            >
              Launch
            </TableCell>
            <TableCell className="cell-market" sx={{ width: colWidths.market, minWidth: 60 }}>
              Cadence
            </TableCell>
            <TableCell className="cell-campaign" sx={{ width: colWidths.campaign, minWidth: 120 }}>
              Destinations
            </TableCell>
            <TableCell className="cell-consumer" sx={{ width: colWidths.consumer, minWidth: 60 }}>
              #consumer
            </TableCell>
            <TableCell
              className="cell-actions"
              sx={{ width: colWidths.actions, minWidth: 50 }}
            ></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{renderRows(rows)}</TableBody>
      </Table>
    </TableContainer>
  );
}

export default CampaignsTableTree;
